import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  ExternalLink, 
  CheckCircle2, 
  AlertCircle, 
  ShieldCheck, 
  Send, 
  Loader2, 
  Trash2, 
  Info,
  ChevronRight,
  Zap,
  Gift
} from 'lucide-react';
import type { FiscalConfig, FiscalTestResponse } from '../../../api/fiscalApi';
import fiscalApi from '../../../api/fiscalApi';

const ALEGRA_AFFILIATE_LINK = import.meta.env.VITE_ALEGRA_AFFILIATE_LINK || 'https://mi.alegra.com/integrations/api';

const FiscalSetupWizard: React.FC = () => {
  const [config, setConfig] = useState<FiscalConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    providerType: 'ALEGRA',
    email: '',
    token: ''
  });
  const [testResult, setTestResult] = useState<FiscalTestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setIsLoading(true);
    try {
      const data = await fiscalApi.getConfig();
      setConfig(data);
      if (data) {
        setStep(3); // Already configured
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTest = async () => {
    if (!formData.email || !formData.token) {
      setError('Por favor completa el correo y el token');
      return;
    }

    setIsSaving(true);
    setError(null);
    setTestResult(null);

    try {
      const result = await fiscalApi.testConnection(formData);
      setTestResult(result);
      if (result.success) {
        setError(null);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError('Error al conectar con Alegra. Verifica tus credenciales.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const newConfig = await fiscalApi.saveConfig(formData);
      setConfig(newConfig);
      setStep(3);
    } catch (err) {
      setError('Error al guardar la configuración');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de que deseas desconectar la facturación electrónica?')) return;
    
    setIsSaving(true);
    try {
      await fiscalApi.deleteConfig();
      setConfig(null);
      setStep(1);
      setFormData({ providerType: 'ALEGRA', email: '', token: '' });
      setTestResult(null);
    } catch (err) {
      setError('Error al eliminar');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Premium Header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-purple-600 to-emerald-600 p-8 text-white shadow-2xl">
        <div className="absolute top-0 right-0 p-4 opacity-10">
          <FileText size={120} />
        </div>
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold tracking-wider uppercase">
              <Zap size={14} className="text-yellow-400 fill-yellow-400" />
              Nuevo: Facturación Electrónica
            </div>
            <h2 className="text-3xl font-extrabold tracking-tight">Cresta + Alegra</h2>
            <p className="text-white/80 max-w-md">
              Genera facturas legales automáticamente cada vez que completes una cita. Cumple con la DIAN sin esfuerzo extra.
            </p>
          </div>
          
          <div className="flex flex-col gap-3">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-2xl flex items-center gap-4">
              <div className="bg-yellow-400 text-indigo-900 p-2 rounded-xl">
                <Gift size={24} />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider opacity-70">Beneficio Alegra</p>
                <p className="text-lg font-bold">1 mes GRATIS extra</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wizard Steps */}
      {config ? (
        <div className="bg-emerald-50 border border-emerald-100 rounded-3xl p-8 flex flex-col md:flex-row items-center gap-8 shadow-sm">
          <div className="bg-emerald-100 p-4 rounded-3xl text-emerald-600">
            <ShieldCheck size={48} />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-xl font-bold text-gray-900">Conexión Activa con Alegra</h3>
            <p className="text-gray-600 mt-1">
              Las facturas se generarán automáticamente para el correo: <span className="font-semibold">{config.maskedEmail}</span>
            </p>
            <div className="mt-4 flex flex-wrap gap-3 justify-center md:justify-start">
              <span className="px-3 py-1 bg-emerald-600 text-white text-xs font-bold rounded-full uppercase">Funcional</span>
              <span className="px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">Alegra API v1</span>
            </div>
          </div>
          <button 
            onClick={handleDelete}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-3 bg-red-50 text-red-600 hover:bg-red-100 font-bold rounded-2xl transition-all"
          >
            <Trash2 size={18} />
            Desconectar
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-12 gap-6">
          {/* Main Wizard Area */}
          <div className="md:col-span-8 bg-white rounded-3xl border border-gray-100 shadow-xl overflow-hidden">
            {/* Tabs / Step Indicator */}
            <div className="flex border-b border-gray-100">
              {[1, 2].map((s) => (
                <div 
                  key={s}
                  className={`flex-1 py-4 text-center text-sm font-bold transition-all ${step === s ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/30' : 'text-gray-400'}`}
                >
                  Paso {s}: {s === 1 ? 'Crear Cuenta' : 'Conectar Token'}
                </div>
              ))}
            </div>

            <div className="p-8">
              {step === 1 && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                  <div className="space-y-2">
                    <h4 className="text-2xl font-bold text-gray-900">Comienza con Alegra</h4>
                    <p className="text-gray-500">
                      Crea tu cuenta en Alegra usando nuestro enlace de aliado para activar beneficios exclusivos de integración.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <a 
                      href={ALEGRA_AFFILIATE_LINK} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="group p-6 bg-gradient-to-br from-gray-50 to-white hover:from-indigo-50 hover:to-white border border-gray-100 hover:border-indigo-200 rounded-3xl transition-all shadow-sm hover:shadow-md"
                    >
                      <div className="flex flex-col gap-4">
                        <div className="bg-indigo-100 text-indigo-600 p-3 rounded-2xl w-fit group-hover:scale-110 transition-transform">
                          <ExternalLink size={24} />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">Crear Cuenta</p>
                          <p className="text-xs text-gray-500 mt-1">Regístrate en pocos minutos</p>
                        </div>
                      </div>
                    </a>

                    <button 
                      onClick={() => setStep(2)}
                      className="group p-6 bg-gradient-to-br from-gray-50 to-white hover:from-emerald-50 hover:to-white border border-gray-100 hover:border-emerald-200 rounded-3xl transition-all shadow-sm hover:shadow-md text-left"
                    >
                      <div className="flex flex-col gap-4">
                        <div className="bg-emerald-100 text-emerald-600 p-3 rounded-2xl w-fit group-hover:scale-110 transition-transform">
                          <ChevronRight size={24} />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">Ya tengo cuenta</p>
                          <p className="text-xs text-gray-500 mt-1">Configurar mis credenciales</p>
                        </div>
                      </div>
                    </button>
                  </div>

                  <div className="bg-blue-50 border border-blue-100 p-5 rounded-2xl flex gap-4">
                    <Info className="text-blue-500 shrink-0" size={20} />
                    <p className="text-sm text-blue-800 leading-relaxed">
                      <b>Importante:</b> Alegra es un Proveedor Tecnológico autorizado por la DIAN. Cresta enviará tus ventas automáticamente a Alegra para generar el XML legal.
                    </p>
                  </div>

                  <button 
                    onClick={() => setStep(2)}
                    className="w-full py-4 bg-gray-900 text-white font-bold rounded-2xl hover:bg-gray-800 transition-all flex items-center justify-center gap-2 group"
                  >
                    Siguiente Paso
                    <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
                  <div className="space-y-2">
                    <h4 className="text-2xl font-bold text-gray-900">Configuración de API</h4>
                    <p className="text-gray-500">
                      Ingresa tus datos de acceso a la API de Alegra que encontrarás en <b>Configuración {">"} API</b> en tu panel de Alegra.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">Correo de Alegra</label>
                      <input 
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="tu@correo.com"
                        className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all font-medium"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1.5 ml-1">Token de API</label>
                      <input 
                        type="password"
                        value={formData.token}
                        onChange={(e) => setFormData({...formData, token: e.target.value})}
                        placeholder="c36b351..."
                        className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all font-mono"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl flex items-center gap-3 text-sm font-medium">
                      <AlertCircle size={18} />
                      {error}
                    </div>
                  )}

                  {testResult?.success && (
                    <div className="p-4 bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-2xl flex items-center justify-between text-sm font-bold">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 size={18} />
                        ¡Conectado a {testResult.companyName}!
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-4 pt-2">
                    <button 
                      onClick={() => setStep(1)}
                      className="flex-1 py-4 bg-gray-100 text-gray-600 font-bold rounded-2xl hover:bg-gray-200 transition-all"
                    >
                      Atrás
                    </button>
                    {!testResult?.success ? (
                      <button 
                        onClick={handleTest}
                        disabled={isSaving}
                        className="flex-[2] py-4 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 flex items-center justify-center gap-2"
                      >
                        {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                        Probar Conexión
                      </button>
                    ) : (
                      <button 
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex-[2] py-4 bg-emerald-600 text-white font-bold rounded-2xl hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200"
                      >
                        {isSaving ? <Loader2 size={18} className="animate-spin" /> : 'Activar Facturación'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar / Info Area */}
          <div className="md:col-span-4 space-y-6">
            <div className="p-6 bg-gray-50 border border-gray-100 rounded-3xl space-y-4">
              <h5 className="font-bold text-gray-900 border-b border-gray-200 pb-3">Preguntas Frecuentes</h5>
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest">¿Es automático?</p>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">Sí, una vez configurado, cada cita marcada como 'Completada' generará la factura en Alegra.</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest">¿Y los impuestos?</p>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">Cresta enviará el IVA configurado en cada uno de tus servicios.</p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-indigo-50 border border-indigo-100 rounded-3xl flex flex-col items-center text-center space-y-3">
              <div className="bg-white p-3 rounded-2xl shadow-sm">
                <ShieldCheck className="text-indigo-600" size={32} />
              </div>
              <h5 className="font-bold text-indigo-900">Seguridad Total</h5>
              <p className="text-xs text-indigo-700/80 leading-relaxed">
                Tus credenciales de API se almacenan con cifrado de grado militar <span className="font-bold">AES-256-GCM</span>. Solo el sistema de facturación puede acceder a ellas.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiscalSetupWizard;
